import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Form } from "react-bootstrap";
import Select from "react-select";

import { request } from "hooks/useOkoApi/useOkoApi";

import { SimulationResult } from "./SimulationResult";
import { StudentPicker } from "./StudentPicker";

const STRATEGY_OPTIONS = [
  { value: "by_readiness", label: "By Readiness" },
  { value: "mixed_readiness", label: "Mixed Readiness" },
];

/**
 * The Simulation tab. Ad-hoc flow (classroom-independent):
 *   school dropdown -> student picker (search/paginate + checkboxes) ->
 *   target type {standard, domain} + target value -> strategy.
 * "Run simulation" POSTs the selected student_ids + target + strategy to
 * /classrooms/group-recommendations/ad-hoc and renders the tier cards. Manual
 * prereq/extension overrides apply to Mixed Readiness. On-screen only.
 */
export const SimulationTab = () => {
  const [schools, setSchools] = useState([]);
  const [schoolId, setSchoolId] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [targetKind, setTargetKind] = useState("standard");
  const [target, setTarget] = useState("");
  const [strategy, setStrategy] = useState("by_readiness");
  const [allStandards, setAllStandards] = useState([]);
  const [prereqStandards, setPrereqStandards] = useState([]);
  const [extensionStandards, setExtensionStandards] = useState([]);
  const [response, setResponse] = useState(null);
  const [runError, setRunError] = useState(null);
  const [pickerError, setPickerError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [schoolData, codes] = await Promise.all([
          request("admin/schools", { method: "GET" }),
          request("standards/codes", { method: "GET" }),
        ]);
        setSchools(schoolData?.schools || []);
        setAllStandards(Array.isArray(codes) ? codes : codes?.codes || []);
      } catch (e) {
        setPickerError(e?.detail || "Failed to load schools/standards");
      }
    })();
  }, []);

  // Reset selection + result when the school changes.
  useEffect(() => {
    setSelectedIds([]);
    setResponse(null);
  }, [schoolId]);

  const runSimulation = useCallback(async () => {
    if (selectedIds.length === 0 || !target) return;
    setRunError(null);
    const payload = {
      student_ids: selectedIds,
      standard: target,
      strategy,
      target_kind: targetKind,
    };
    if (strategy === "mixed_readiness") {
      payload.prereq = prereqStandards.map((s) => s.value);
      payload.extension = extensionStandards.map((s) => s.value);
    }
    try {
      const data = await request("classrooms/group-recommendations/ad-hoc", {
        method: "POST",
        payload,
      });
      setResponse(data);
    } catch (e) {
      setResponse(null);
      setRunError({ status: e?.status, detail: e?.detail });
    }
  }, [
    selectedIds,
    target,
    strategy,
    targetKind,
    prereqStandards,
    extensionStandards,
  ]);

  const runnable = selectedIds.length > 0 && Boolean(target);

  return (
    <div className="simulation-tab">
      {pickerError && (
        <Alert variant="danger" data-testid="picker-error-panel">
          {pickerError}
        </Alert>
      )}

      <Form.Group className="school-picker">
        <Form.Label>School</Form.Label>
        <Form.Control
          as="select"
          data-testid="school-select"
          value={schoolId}
          onChange={(e) => setSchoolId(e.target.value)}
        >
          <option value="">Select a school…</option>
          {schools.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Form.Control>
      </Form.Group>

      <StudentPicker
        schoolId={schoolId}
        selectedIds={selectedIds}
        onChange={setSelectedIds}
      />

      {schoolId && (
        <>
          <Form.Group className="target-kind-picker">
            <Form.Label>Target type</Form.Label>
            <Form.Control
              as="select"
              data-testid="target-kind-select"
              value={targetKind}
              onChange={(e) => {
                setTargetKind(e.target.value);
                setTarget("");
              }}
            >
              <option value="standard">Standard (CCSS)</option>
              <option value="domain">Domain (e.g. NWEA MAP)</option>
            </Form.Control>
          </Form.Group>

          <Form.Group className="target-picker">
            <Form.Label>
              {targetKind === "domain" ? "Target domain" : "Target standard"}
            </Form.Label>
            {targetKind === "domain" ? (
              <Form.Control
                type="text"
                data-testid="target-input"
                placeholder="e.g. Geometry"
                value={target}
                onChange={(e) => setTarget(e.target.value)}
              />
            ) : (
              <Form.Control
                as="select"
                data-testid="target-input"
                value={target}
                onChange={(e) => setTarget(e.target.value)}
              >
                <option value="">Select a standard…</option>
                {allStandards.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Form.Control>
            )}
          </Form.Group>

          <Form.Group className="strategy-picker">
            <Form.Label>Strategy</Form.Label>
            <Form.Control
              as="select"
              data-testid="strategy-select"
              value={strategy}
              onChange={(e) => setStrategy(e.target.value)}
            >
              {STRATEGY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Form.Control>
          </Form.Group>

          {strategy === "mixed_readiness" && (
            <>
              <Form.Group className="prereq-picker">
                <Form.Label>Prerequisite standards (optional override)</Form.Label>
                <Select
                  isMulti
                  inputId="prereq-select"
                  classNamePrefix="prereq-select"
                  placeholder="Search standards…"
                  options={allStandards.map((c) => ({ value: c, label: c }))}
                  value={prereqStandards}
                  onChange={(v) => setPrereqStandards(v || [])}
                />
              </Form.Group>
              <Form.Group className="extension-picker">
                <Form.Label>Extension standards (optional override)</Form.Label>
                <Select
                  isMulti
                  inputId="extension-select"
                  classNamePrefix="extension-select"
                  placeholder="Search standards…"
                  options={allStandards.map((c) => ({ value: c, label: c }))}
                  value={extensionStandards}
                  onChange={(v) => setExtensionStandards(v || [])}
                />
              </Form.Group>
            </>
          )}
        </>
      )}

      <Button
        data-testid="run-simulation-btn"
        disabled={!runnable}
        onClick={runSimulation}
        style={{ marginTop: "12px" }}
      >
        Run simulation
      </Button>

      <div style={{ marginTop: "24px" }}>
        <SimulationResult response={response} error={runError} />
      </div>
    </div>
  );
};
